import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  accountProjectAccessQueryOptions,
  type CustomerAccountListItem,
} from '@/entities/customer-account'
import { companyProjectsQueryOptions } from '@/entities/project'
import { flattenPages } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Dialog } from '@/shared/ui/dialog'
import { Field } from '@/shared/ui/field'
import { Icon } from '@/shared/ui/icon'
import { Select } from '@/shared/ui/select'
import { Spinner } from '@/shared/ui/spinner'
import { toast } from '@/shared/ui/toast'
import { useGrantAccess, useRevokeAccess } from '../api/grant'

interface GrantProjectAccessDialogProps {
  account: CustomerAccountListItem
  companyId: string
  open: boolean
  onClose: () => void
}

// Выдача/отзыв доступа человека к проектам компании.
export function GrantProjectAccessDialog({
  account,
  companyId,
  open,
  onClose,
}: GrantProjectAccessDialogProps) {
  const accessQ = useQuery(accountProjectAccessQueryOptions(account.id))
  const projectsQ = useInfiniteQuery(companyProjectsQueryOptions(companyId))
  const grant = useGrantAccess(account.id, companyId)
  const revoke = useRevokeAccess(account.id, companyId)
  const [pick, setPick] = useState('')

  const granted = accessQ.data ?? []
  const grantedIds = new Set(granted.map((g) => g.project_id))
  const available = flattenPages(projectsQ.data?.pages).filter((d) => !grantedIds.has(d.id))
  const busy = grant.isPending || revoke.isPending

  function add() {
    if (!pick) return
    grant.mutate(pick, {
      onSuccess: () => {
        toast.success('Доступ выдан')
        setPick('')
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось выдать'),
    })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Доступ к проектам — ${account.full_name}`}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Готово
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {accessQ.isPending ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : granted.length === 0 ? (
          <p className="text-sm text-text-tertiary">Доступ к проектам не выдан.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {granted.map((g) => (
              <li
                key={g.project_id}
                className="flex items-center gap-2 rounded-control bg-bg-sunken px-3 py-2 text-sm"
              >
                <span className="min-w-0 flex-1 truncate">{g.name ?? g.project_id}</span>
                <button
                  type="button"
                  aria-label={`Отозвать доступ к «${g.name ?? g.project_id}»`}
                  disabled={busy}
                  onClick={() =>
                    revoke.mutate(g.project_id, {
                      onSuccess: () => toast.success('Доступ отозван'),
                      onError: () => toast.error('Не удалось отозвать'),
                    })
                  }
                  className="shrink-0 rounded-control p-1.5 text-text-tertiary hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent disabled:opacity-50"
                >
                  <Icon name="close" size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-end gap-2 border-border-subtle border-t pt-4">
          <div className="min-w-0 flex-1">
            <Field label="Выдать проект">
              {(props) => (
                <Select
                  {...props}
                  value={pick}
                  onChange={setPick}
                  disabled={busy}
                  placeholder="Выберите проект"
                  options={available.map((d) => ({ value: d.id, label: d.title }))}
                />
              )}
            </Field>
          </div>
          <Button onClick={add} disabled={busy || !pick}>
            Выдать
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
